import React, { useCallback, useRef, useState } from 'react';
import styles from './Table.module.scss';
import { Body } from './Body';
import { Head } from './Head';
import { TableProps } from 'new/elements/Table/types';
import { useTableContext } from 'new/elements/Table/useTableContext';
import { TableContextProvider } from 'new/elements/Table/TableContext';
import { Skeleton, Box, Typography } from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';
import { ListUnvirtualized } from 'new/elements/Table/ListUnvirtualized';
import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';

function LocalTable<T>(props: TableProps<T>) {
  const {
    columns = [],
    rows: propsRows = [],
    headerHeight = 48,
    height,
    width,
    loading,
    getRowClassName,
    keyExtractor,
    disableVirtualization,
    parentScrollRef,
    isRowOpen,
    onEndReached,
    totalCount,
    EmptyComponent,
    emptyStateText,
    skeletonModeLength = 10,
    showCheckbox = true,
    localTableInnerContainerClassName,
    ...tableProps
  } = props;

  const tableRef = useRef();
  const headerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(props.initialOpenData || null);
  const { sortedRows: rows, rows: originalRows } = useTableContext<T>();
  const hasOriginalRows = originalRows && Array.isArray(originalRows) && originalRows.length > 0;

  /**
   * This variable is a boolean that determines if `isRowOpen` is a function, implying that
   * the opening and closing behavior of a specific row is managed externally by the parent.
   *
   * If `isOpenControlledByParent` is true, the parent component provides the functionality
   * to determine the state of a row's openness. If false, the component manages the state internally.
   */
  const isOpenControlledByParent = typeof isRowOpen === 'function';

  useDidUpdateEffect(
    (oldInputs) => {
      if (!oldInputs?.[0]) {
        setOpen(props.initialOpenData);
      }
    },
    [props.initialOpenData],
  );

  if (!columns || !columns.length) return null;

  /**
   * This function is used to expand or collapse a row.
   * 1. If the row is already open, it will be collapsed.
   * 2. If the row is not open, it will be expanded.
   * Uses shouldRowExpand if provided for comparison, otherwise falls back to object reference equality.
   */
  const onClickExpand = useCallback(
    (data: React.SetStateAction<T>) => {
      const isCurrentlyOpen = tableProps.shouldRowExpand
        ? tableProps.shouldRowExpand(open, data as T)
        : data === open;

      if (isCurrentlyOpen) {
        setOpen(null);
        return;
      }
      setOpen(data);
    },
    [open, tableProps.shouldRowExpand],
  );

  const endReached = useDebouncedCallback(onEndReached, [onEndReached], 400);

  const TableComponent = disableVirtualization ? ListUnvirtualized : Virtuoso;
  const itemContentFn = useCallback(
    (index: React.Key, row: T) => {
      const disableRowHighlight = getRowClassName?.(row) !== styles.selectedRow;

      const isRowOpened = isOpenControlledByParent ? isRowOpen(row) : open;

      if (loading) {
        return (
          <div className={getRowClassName?.(row)} key={index}>
            <Body
              rowData={row}
              index={index}
              open={undefined}
              onClickExpand={undefined}
              disableRowHighlight={disableRowHighlight}
              {...props}
              columns={props.columns.map((column) => ({
                ...column,
                renderRow: () => (
                  <Skeleton
                    animation="wave"
                    key={index}
                    variant="rounded"
                    width="80%"
                    height={20}
                    sx={{
                      borderRadius: 'var(--border-radius)',
                      padding: 'var(--spacing-lg)',
                    }}
                  />
                ),
              }))}
            />
          </div>
        );
      }

      return (
        <div className={getRowClassName?.(row)} key={keyExtractor ? keyExtractor(row) : index}>
          <Body
            key={keyExtractor ? keyExtractor(row) : index}
            rowData={row}
            open={isRowOpened}
            index={index}
            onClickExpand={isOpenControlledByParent ? undefined : onClickExpand}
            disableRowHighlight={disableRowHighlight}
            {...props}
          />
        </div>
      );
    },
    [
      loading,
      getRowClassName,
      onClickExpand,
      open,
      props.columns,
      props.getRowTdClassName,
      props.rowCollapsibleRender,
      props.rowEndRender,
      props.onRowClick,
      props.onSelectedRowsChange,
      showCheckbox,
      props.isRowExpandable,
      props.isRowSelectable,
      props.accessoryData,
      props.collapseOnRowClick,
      isRowOpen,
      keyExtractor,
    ],
  );

  const showEmptyState = !loading && !rows.length;

  if (!hasOriginalRows && !loading && EmptyComponent) return EmptyComponent;

  return (
    <div
      ref={tableRef}
      className={styles.table}
      aria-label="local-table-cotainer"
      style={
        !!parentScrollRef
          ? { width: width || '100%' }
          : {
              height,
              width: width || '100%',
            }
      }
    >
      <div
        ref={headerRef}
        aria-label="local-table-inner-cotainer"
        className={localTableInnerContainerClassName}
      >
        <Head {...tableProps} rows={rows} columns={columns} />
      </div>

      {showEmptyState ? (
        <div className={styles.emptyState}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
            <Typography variant="body2" color="text.secondary">
              {emptyStateText || ''}
            </Typography>
          </Box>
        </div>
      ) : (
        /* @ts-ignore */
        <TableComponent
          style={
            !!parentScrollRef
              ? undefined
              : { height: height - (headerRef?.current?.offsetHeight || 0) }
          }
          useWindowScroll={!!parentScrollRef}
          endReached={!!onEndReached ? endReached : undefined}
          customScrollParent={parentScrollRef?.current || (height ? undefined : tableRef?.current)}
          // @ts-ignore
          data={loading ? Array(skeletonModeLength).fill(null) : rows}
          totalCount={loading ? 10 : totalCount || rows.length}
          itemContent={itemContentFn}
        />
      )}
    </div>
  );
}

export function Table<T>(props: TableProps<T>) {
  return (
    <TableContextProvider {...props}>
      <LocalTable {...props} />
    </TableContextProvider>
  );
}
